import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import DataBase from "../../dataBase/liquidGuides";
import { REPORT_REASONS } from "../../Common/shareInvites";
import { sharingActionNow, getSharingState } from "../../Common/useInvitationCount";

// "Report this guide" (4.10.55, 2026-10-26). One dialog for the whole app,
// mounted once in App.js; the menus open it with openReportGuide(). The
// report goes to guide_reports (read by the admin in the Admin Dashboard)
// and the guide is hidden from the reporter's Library.

const OPEN_EVENT = "andiamo:report-guide";

export const openReportGuide = () => window.dispatchEvent(new Event(OPEN_EVENT));

// Offered for a saved guide someone else made, to a signed-in person.
export const canReportGuide = (guide_meta, user_info) => {
  const uid = user_info?.uid;
  const owner = guide_meta?.guide_info?.user_id;
  // not again for a guide this person already reported (or hid) in this session's record
  const hidden = getSharingState()?.hidden || [];
  return !!uid && uid !== "guest" && !!guide_meta?.guide_info_id && !!owner && owner !== uid && !hidden.includes(guide_meta.guide_info_id);
};

const ReportGuideModal = () => {
  const guide_meta = useSelector((state) => state.guide_meta);
  const user_info = useSelector((state) => state.user_info);
  const [show, setShow] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [status, setStatus] = useState("form"); // form | sending | done | error

  useEffect(() => {
    const open = () => {
      setReason("");
      setDetails("");
      setStatus("form");
      setShow(true);
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  if (!show) return null;
  const close = () => setShow(false);
  const reportable = canReportGuide(guide_meta, user_info);

  const send = () => {
    setStatus("sending");
    const info = guide_meta.guide_info || {};
    DataBase.addGuideReport(
      {
        guide_info_id: guide_meta.guide_info_id,
        guide_name: info.guide_name || "",
        owner_uid: info.user_id || "",
        owner_name: info.owner_name || "",
        reporter_uid: user_info.uid,
        reporter_email: user_info.email || "",
        reason,
        details: details.trim().slice(0, 1000),
      },
      (res) => {
        if (res.pass !== true) {
          setStatus(res.already ? "already" : "error");
          return;
        }
        sharingActionNow(user_info.uid, "hide", guide_meta.guide_info_id);
        setStatus("done");
      }
    );
  };

  return (
    <Modal show onHide={close} centered size="sm" style={{ zIndex: 70000 }}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>Report this guide</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!reportable ? (
          <p>Only a saved guide made by someone else can be reported, and you need to be signed in.</p>
        ) : status === "done" ? (
          <p>Thank you. We'll review this guide, usually within a day. It won't appear in your Library's Featured or Shared lists any more.</p>
        ) : (
          <>
            <p style={{ marginBottom: 8 }}>What's wrong with "{guide_meta.guide_info?.guide_name || "this guide"}"?</p>
            {REPORT_REASONS.map((r) => (
              <label key={r} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, cursor: "pointer" }}>
                <input type="radio" name="report-reason" checked={reason === r} onChange={() => setReason(r)} />
                {r}
              </label>
            ))}
            <textarea
              className="form-control"
              rows={3}
              maxLength={1000}
              placeholder="Anything else we should know (optional)"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              style={{ marginTop: 6 }}
            />
            {status === "error" && <p style={{ color: "#B91C1C", marginTop: 8 }}>Couldn't send the report. Please try again.</p>}
            {status === "already" && (
              <p style={{ color: "#B91C1C", marginTop: 8 }}>
                Couldn't send the report. You may have reported this guide already — each guide can be reported once per person.
              </p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {reportable && status !== "done" ? (
          <>
            <Button variant="secondary" onClick={close}>Cancel</Button>
            <Button variant="danger" disabled={!reason || status === "sending"} onClick={send}>
              {status === "sending" ? "Sending…" : "Report"}
            </Button>
          </>
        ) : (
          <Button onClick={close}>OK</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReportGuideModal;
